import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { clearControlsForErrorDetails, isFormValid } from '../core/form-helpers';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { ModalComponent } from '../core/modal/modal.component';
import { Customer, Device, Lookup, PagedResults } from '../app.types';
import { DeviceManagementService } from '../services/device-management/device-management.service';
import { HttpErrorService } from '../services/http-error.service';
import { DialogService } from '../services/dialog/dialog.service';
import { createDefaultData } from '../confirm-dialog/confirm-dialog.component';
import { LookupService } from '../services/lookup/lookup.service';
import { startWith, switchMap, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';

@Component({
    selector: 'device-form-modal',
    templateUrl: './device-form-modal.component.html',
    styleUrls: ['./device-form-modal.component.scss'],
    standalone: false
})
export class DeviceFormModalComponent implements OnInit, OnDestroy {
  @Input() deviceId: string;
  @Input() triggerRefresh: Subject<null>;
  @Output() close: EventEmitter<boolean> = new EventEmitter();

  @ViewChild(ModalComponent) modal: ModalComponent;

  private _customers: Customer[] = [];

  @Input() set customers(value: Customer[]) {
    this._customers = value ?? [];
    this.customerOptions = this._customers.map(x => ({ key: x.id, value: x.name }));
  }

  get customers(): Customer[] {
    return this._customers;
  }

  customerOptions: { key: string; value: string }[] = [];
  assetTypeOptions: { key: string; value: string }[] = [];

  readonly serialNumberErrors = { required: "Please enter the device's serial number" };
  readonly fleetIdErrors = { required: "Please enter the device's asset ID" };

  assetTypes: Lookup[] = [];
  newAssetTypeID: string = null;

  form: FormGroup;
  requestInProgress = false;

  addingAssetType = false;

  refreshAssetTypes$ = new Subject<string>();
  destroy$ = new Subject();

  /**
   * CTOR
   */
  constructor(
    private formBuilder: FormBuilder,
    private deviceManagementService: DeviceManagementService,
    private readonly dialogService: DialogService,
    private lookupService: LookupService,
    private readonly httpErrorService: HttpErrorService
  ) {
    this.form = formBuilder.group({
      serialNumber: ['', Validators.required],
      customerId: ['', Validators.required],
      fleetId: ['', Validators.required],
      assetTypeId: [''],
      assetGroupPath: [''],
      APIKey: ['', Validators.minLength(0)],
      enabled: [false],
      enableRecordingMapRequest: [false],
      enableCamerasSnapshotRequest: [false]
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  /**
   * On Init
   */
  ngOnInit() {
    if (this.deviceId != null)
      {this.deviceManagementService.getDevice(this.deviceId).subscribe((device: Device) => {
        this.form.setValue({
          serialNumber: device.serialNumber,
          customerId: device.customer == null ? null : device.customer.id,
          fleetId: device.fleetId,
          assetTypeId: device.assetType?.id == null ? null : device.assetType.id,
          APIKey: (device.apiKey == null || device.apiKey.length==0) ? '' : device.apiKey,
          enabled: device.enabled,
          enableRecordingMapRequest: device.enableRecordingMapRequest,
          enableCamerasSnapshotRequest: device.enableCamerasSnapshotRequest,
          assetGroupPath: (device.assetGroupPath == null || device.assetGroupPath.length==0) ? '' : device.assetGroupPath,
        });
      });}

    this.refreshAssetTypes$.pipe(
      startWith(''),
      switchMap(x => {
        const assetTypes$: Observable<PagedResults<Lookup>> = this.lookupService.getList('AssetType', {});
        return combineLatest([assetTypes$, of(x)])
      }),
      takeUntil(this.destroy$)
    ).subscribe(([data, newAssetTypeID]) => {
      this.assetTypes = data.results;
      this.assetTypeOptions = [{ key: null, value: 'None' }, ...this.assetTypes.map(x => ({ key: x.id, value: x.name }))];
      if(newAssetTypeID) {
        this.form.controls['assetTypeId'].setValue(newAssetTypeID);
      }
      if(!this.assetTypes.some(x => x.id === this.form.controls['assetTypeId'].value)) {
        this.form.controls['assetTypeId'].setValue(null);
      }
    });
  }
  

  submitForm() {
    if (!isFormValid(this.form)) {return;}

    const formData = this.form.getRawValue();
    formData.name = this.customers.find(x => x.id === formData.customerId)?.name;
    this.requestInProgress = true;

    // ADD
    if (this.deviceId == null) {
      const addConfirmText = 'Are you sure you wish to add device?';

      this.dialogService.confirm(createDefaultData(addConfirmText)).subscribe((result) => {
        console.log(`Dialog result: ${result}`);

        if (result == null) {return;}

        if (result == false) {console.log("don't add device ");}

        if (result == true) {
          console.log('add  device ');

          this.deviceManagementService.addDevice(formData).subscribe((res) => {
            this.handleSaveResponse(res);
          });
        }
      });
    }

    // EDIT
    else {
      const editConfirmText = 'Are you sure you wish to save changes for  ' + this.form.get('fleetId').value + '?';

      this.dialogService.confirm(createDefaultData(editConfirmText)).subscribe((result) => {
        console.log(`Dialog result: ${result}`);

        if (result == null) {return;}

        if (result == false) {console.log("don't save changes for " + this.form.get('fleetId').value);}

        if (result == true) {
          console.log('save changes for device ' + this.form.get('fleetId').value);

          this.deviceManagementService.editDevice({ ...formData, id: this.deviceId }).subscribe((res) => {
            this.handleSaveResponse(res);
          });
        }
      });
    }
  }

  /**
   * Closes the modal on a successful save. A rejected save keeps the modal open so the user can
   * correct it, clearing any field the server named as the cause.
   * @param res the response, or the swallowed error the service hands back in its place
   */
  private handleSaveResponse(res: unknown) {
    if (res instanceof HttpErrorResponse) {
      clearControlsForErrorDetails(this.form, this.httpErrorService.getErrorDetails(res));
      return;
    }

    if (res != null) {
      this.triggerRefresh.next(null);
      this.modal.closeModal();
    }
  }

  /**
   * Deletes a device
   */
  deleteDevice() {
    const cancelConfirmText =
      'Are you sure you wish to delete device ' +
      this.form.get('fleetId').value +
      '?' +
      ' All related data (Health, Location, VideoExports Jobs) will be deleted too!';

    this.dialogService.confirm(createDefaultData(cancelConfirmText)).subscribe((result) => {
      console.log(`Dialog result: ${result}`);

      if (result == null) {return;}

      if (result == false) {console.log("don't delete device export");}

      if (result == true) {
        console.log('delete device ' + this.form.get('fleetId').value);
        if (this.deviceId) {
          this.deviceManagementService.deleteDevice(this.deviceId).subscribe((res) => {
            if (res != null) {
              this.triggerRefresh.next(null);
              this.modal.closeModal();
            }
          });
        }
      }
    });
  }

  /**
   * Modal Cleanup
   */
  modalCleanup() {
    // if a request was triggered, emit true to trigger a refresh of device table
    this.close.emit(this.requestInProgress);
    this.requestInProgress = false;
  }

  openAssetTypeExpander(){
    this.addingAssetType = true;
  }

  assetTypeExpanderClosed(assetTypeId: string) {
    this.addingAssetType = false;

    this.refreshAssetTypes$.next(assetTypeId);
  }

  assetTypeDeleted() {
    this.refreshAssetTypes$.next(null);
  }
}
